import { REDIRECT_QUERY_PARAM } from '../redirectTarget'
import {
  isShopProductEditorPath,
  isShopProductTypesPath,
  normalizeLegacyAdminPath,
} from '../section-routing/pathRoutes'
import { buildDashboardPath } from '../useAdminSectionRouting.helpers'
import type { UseAdminSectionRoutingParams } from '../useAdminSectionRouting.types'

type SyncLocationParams = Pick<
  UseAdminSectionRoutingParams,
  'isAuthenticated' | 'section' | 'selectedUserId' | 'selectedShopProductId' | 'pageEditorMode' | 'pageEditorId'
>

export const syncLocationForAuthenticatedSection = ({
  isAuthenticated,
  section,
  selectedUserId,
  selectedShopProductId,
  pageEditorMode,
  pageEditorId,
}: SyncLocationParams) => {
  if (!isAuthenticated) return

  const params = new URLSearchParams(window.location.search)
  const nextSection = section

  // Preserve detail state when canonicalizing legacy browser URLs. Without
  // this, a refresh of `/admin/orders/:id` or `/admin/customers/:id` would
  // open the list instead of the detail view after the path is normalized.
  const legacyOrderMatch = window.location.pathname.match(/^\/admin\/orders\/([^/?#]+)\/?$/)
  const legacyCustomerMatch = window.location.pathname.match(/^\/admin\/customers\/([^/?#]+)\/?$/)
  if (nextSection === 'shop-orders' && !params.get('orderId') && legacyOrderMatch?.[1]) {
    params.set('orderId', decodeURIComponent(legacyOrderMatch[1]))
  }
  if (nextSection === 'customers' && !params.get('customerId') && legacyCustomerMatch?.[1]) {
    params.set('customerId', decodeURIComponent(legacyCustomerMatch[1]))
  }

  params.delete('section')
  params.delete(REDIRECT_QUERY_PARAM)
  params.delete('userId')

  if (nextSection === 'pages') {
    params.set('pageMode', pageEditorMode)
    if (pageEditorId) params.set('pageId', pageEditorId)
    else params.delete('pageId')
  } else {
    params.delete('pageMode')
    params.delete('pageId')
  }

  if (nextSection !== 'customers') {
    params.delete('customerId')
  }
  if (nextSection !== 'shop-orders') {
    params.delete('orderId')
  }
  if (nextSection !== 'shop-orders' && nextSection !== 'shop-products') {
    params.delete('status')
    params.delete('q')
    params.delete('lowStock')
    params.delete('uncategorized')
    params.delete('missingImage')
  }
  if (nextSection !== 'audit-logs') {
    params.delete('auditLogId')
    params.delete('auditLogPage')
  }
  if (nextSection !== 'audit-trail') {
    params.delete('auditTrailId')
    params.delete('auditTrailPage')
  }
  if (nextSection !== 'support-inbox') {
    params.delete('supportRequestId')
    params.delete('supportMode')
  }

  if (section !== 'plugins') {
    params.delete('pluginView')
    params.delete('pluginId')
    params.delete('plugin_view')
    params.delete('plugin_id')
  }
  params.delete('productId')
  params.delete('product_type_id')
  params.delete('product_type_field')
  params.delete('emailView')

  const nextPathname =
    nextSection === 'shop-product-types' && isShopProductTypesPath(window.location.pathname)
      ? normalizeLegacyAdminPath(window.location.pathname)
      : nextSection === 'shop-products' && isShopProductEditorPath(window.location.pathname)
        ? normalizeLegacyAdminPath(window.location.pathname)
      : buildDashboardPath(nextSection, selectedShopProductId, selectedUserId)
  const query = params.toString()
  const next = `${nextPathname}${query ? `?${query}` : ''}`
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (next !== current) {
    window.history.replaceState({}, '', next)
  }
}
